/* © NXAPP Limited */
import { useMemo, useState } from 'react'
import Papa from 'papaparse'
import { supabase } from '@/lib/supabase'
import { X, ArrowRight, Loader2 } from 'lucide-react'

/** Built-in contact fields a column can map to. */
const STANDARD = [
  { value: 'email', label: 'Email (required)' },
  { value: 'first_name', label: 'First name' },
  { value: 'last_name', label: 'Last name' },
  { value: 'company', label: 'Company' },
  { value: 'industry', label: 'Industry' },
] as const

type MapTo = 'ignore' | 'custom' | (typeof STANDARD)[number]['value']

const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)

/** Turn a column header into a safe merge-tag key: "Job Title" → jobTitle. */
function slugKey(header: string): string {
  const parts = header.trim().replace(/[^a-zA-Z0-9]+/g, ' ').trim().split(/\s+/)
  if (!parts[0]) return 'field'
  return parts.map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('')
}

/** Best-guess mapping for a header. Unrecognised columns default to a custom
 *  field, so any extra data becomes a usable merge tag by default. */
function guess(header: string): MapTo {
  const h = header.trim().toLowerCase().replace(/[^a-z]/g, '')
  if (['email', 'emailaddress', 'emailid', 'mail', 'e'].includes(h)) return 'email'
  if (['firstname', 'fname', 'givenname', 'first', 'forename'].includes(h)) return 'first_name'
  if (['lastname', 'lname', 'surname', 'familyname', 'last'].includes(h)) return 'last_name'
  if (['company', 'organisation', 'organization', 'firm', 'business', 'companyname', 'employer'].includes(h)) return 'company'
  if (['industry', 'sector', 'vertical'].includes(h)) return 'industry'
  return 'custom'
}

export default function ImportCsvModal({ file, userId, onClose, onDone }: {
  file: File
  userId: string
  onClose: () => void
  onDone: (r: { imported: number; skipped: number }) => void
}) {
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<Record<string, string>[]>([])
  const [mapping, setMapping] = useState<Record<string, MapTo>>({})
  const [parsing, setParsing] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  // Parse once on first render.
  useMemo(() => {
    Papa.parse<Record<string, string>>(file, {
      header: true, skipEmptyLines: true,
      complete: ({ data, meta }) => {
        const hs = (meta.fields ?? []).filter(Boolean)
        setHeaders(hs)
        setRows(data)
        setMapping(Object.fromEntries(hs.map(h => [h, guess(h)])))
        setParsing(false)
      },
      error: () => { setErr('Could not read that CSV file.'); setParsing(false) },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  const emailHeader = headers.find(h => mapping[h] === 'email')
  const customCount = headers.filter(h => mapping[h] === 'custom').length

  async function runImport() {
    if (!emailHeader) { setErr('Map one column to Email — it is required.'); return }
    setErr(''); setBusy(true)
    try {
      // Register any new custom fields so they appear in the merge-tag menu.
      const customCols = headers.filter(h => mapping[h] === 'custom').map(h => ({ header: h, key: slugKey(h), label: h.trim() }))
      // Two headers can slug to the same key — keep the first, ignore later dupes.
      const seen = new Set<string>()
      const uniqCustom = customCols.filter(c => (seen.has(c.key) ? false : (seen.add(c.key), true)))
      if (uniqCustom.length) {
        await supabase.from('contact_custom_fields')
          .upsert(uniqCustom.map(c => ({ user_id: userId, key: c.key, label: c.label })), { onConflict: 'user_id,key', ignoreDuplicates: true })
      }

      const stdMap: Partial<Record<(typeof STANDARD)[number]['value'], string>> = {}
      for (const h of headers) { const m = mapping[h]; if (m !== 'ignore' && m !== 'custom' && m !== 'email') stdMap[m] = h }

      let invalid = 0
      const toInsert: Record<string, unknown>[] = []
      for (const row of rows) {
        const email = (row[emailHeader] || '').trim().toLowerCase()
        if (!email || !isEmail(email)) { invalid++; continue }
        const meta: Record<string, string> = {}
        for (const c of uniqCustom) { const v = (row[c.header] ?? '').trim(); if (v) meta[c.key] = v }
        toInsert.push({
          user_id: userId, email,
          first_name: stdMap.first_name ? (row[stdMap.first_name] || null) : null,
          last_name: stdMap.last_name ? (row[stdMap.last_name] || null) : null,
          company: stdMap.company ? (row[stdMap.company] || null) : null,
          industry: stdMap.industry ? (row[stdMap.industry] || null) : null,
          meta: Object.keys(meta).length ? meta : null,
          source: 'csv', status: 'SUBSCRIBED',
        })
      }

      let imported = 0
      for (let i = 0; i < toInsert.length; i += 500) {
        const chunk = toInsert.slice(i, i + 500)
        const { data, error } = await supabase.from('contacts')
          .upsert(chunk, { onConflict: 'user_id,email', ignoreDuplicates: true }).select('id')
        if (error) throw error
        imported += data?.length ?? 0
      }
      onDone({ imported, skipped: invalid + (toInsert.length - imported) })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Import failed.')
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Import contacts — match your columns</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          {parsing ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Reading file…</div>
          ) : !headers.length ? (
            <p className="text-sm text-red-600 py-8 text-center">No columns found in that file.</p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-3">
                {rows.length} rows found. Choose what each column is. Unrecognised columns become <b>custom fields</b> you can drop into any email as a tag like <code className="font-mono text-brand-700">{'{{jobTitle}}'}</code>.
              </p>
              <div className="space-y-1.5">
                {headers.map(h => {
                  const to = mapping[h]
                  const sample = rows.find(r => (r[h] ?? '').trim())?.[h]?.trim()
                  return (
                    <div key={h} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-1">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{h}</p>
                        {sample && <p className="text-[11px] text-gray-400 truncate">e.g. {sample}</p>}
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-300" />
                      <div className="min-w-0">
                        <select
                          value={to}
                          onChange={e => setMapping(m => ({ ...m, [h]: e.target.value as MapTo }))}
                          className={`input py-1 text-sm ${to === 'custom' ? 'text-brand-700' : ''}`}
                        >
                          {STANDARD.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                          <option value="custom">Custom field → {'{{'}{slugKey(h)}{'}}'}</option>
                          <option value="ignore">Ignore this column</option>
                        </select>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="mt-4 text-[11px] text-gray-500 space-y-1">
                {!emailHeader && <p className="text-amber-600">⚠ Map one column to <b>Email</b> to continue.</p>}
                {customCount > 0 && <p>{customCount} column{customCount === 1 ? '' : 's'} will be saved as custom field{customCount === 1 ? '' : 's'} and added to the tag menu.</p>}
                <p>Existing contacts (same email) are kept as-is and not overwritten.</p>
              </div>
            </>
          )}
          {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button onClick={onClose} className="btn-secondary" disabled={busy}>Cancel</button>
          <button onClick={runImport} disabled={busy || parsing || !emailHeader} className="btn-primary disabled:opacity-50">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : `Import ${rows.length} rows`}
          </button>
        </div>
      </div>
    </div>
  )
}
